import { Skeleton } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../../components/main-heading.component";
import { _get, _post } from "../../../services/api.service";
import "../content-engine.css";
import { contentEngineService } from "../content-engine.service";
import "../../video-create/CreateVideo.css";
import "./TrendingIdeas.css";

interface Topic {
  id: number;
  title: string;
  tag: string;
}

const TrendingIdeas: React.FC = () => {
  const navigate = useNavigate();
  const steps = [
    { label: "Topic", description: "Enter your topic idea", path: "/content-engine" },
    { label: "Format", description: "Choose your content format", path: "/content-engine/select-format" },
    { label: "Scripts", description: "Pick from generated scripts", path: "/content-engine/choose-script" },
    { label: "Refinement", description: "Refine and finalize your script", path: "/content-engine/script-detail" },
  ];
  const currentStep = 0;
  const [topics, setTopics] = useState<Topic[]>([]);
  const [filters, setFilters] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selecting, setSelecting] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [activeTag, setActiveTag] = useState<string>("");
  const [visibleCount, setVisibleCount] = useState<number>(8);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const response = await _get("/content-engine/trending-topics/", {}, true);
        const formattedTopics: Topic[] = [];
        const formattedFilters = Object.keys(response || {}).map((key) => ({
          label: key,
          value: key.replace(/^[^\w]+\s*/, ""),
        }));
        Object.entries(response || {}).forEach(([tag, values], index) => {
          (values as string[]).forEach((title, topicIndex) => {
            formattedTopics.push({
              id: index * 100 + topicIndex,
              title,
              tag,
            });
          });
        });
        setFilters(formattedFilters);
        setTopics(formattedTopics);
      } catch (error) {
        console.error("Error fetching trending topics", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredTopics = useMemo(() => {
    return topics
      .map((item) => ({
        ...item,
        tag: item.tag.replace(/^[^\w]+\s*/, ""),
      }))
      .filter((item) => {
        const matchesSearch = item.title?.toLowerCase().includes(search.toLowerCase());
        const matchesTag = activeTag ? item.tag === activeTag : true;
        return matchesSearch && matchesTag;
      });
  }, [topics, search, activeTag]);

  const visibleTopics = useMemo(() => {
    return [...filteredTopics].sort(() => Math.random() - 0.5).slice(0, visibleCount);
  }, [filteredTopics, visibleCount]);

  const selectTopic = async (item: Topic) => {
    setSelecting(true);
    try {
      const response = await _post("/content-engine/topics/", { title: item.title }, {}, false, true);
      const topicId = response?.id;
      if (!topicId) throw new Error("Topic ID not returned");
      contentEngineService.setSelectedTopic(topicId, item.title);
      navigate("/content-engine/select-format");
    } catch {
      // error already handled by api.service
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="container-fluid trending-ideas-page">
      <MainHeading
        title="Trending Topics"
        subTitle="Explore what's trending right now — filter by category to find high-performing content ideas"
      />

      <div className="cv-workspace">
        <aside className="cv-progress-panel" aria-label="Content engine progress">
          <div className="cv-progress-caption">CONTENT ENGINE</div>
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = index === currentStep;
            return (
              <div
                key={step.label}
                className={`cv-progress-step${complete ? " complete" : ""}${active ? " active" : ""}${complete ? " clickable" : ""}`}
                onClick={() => complete && navigate(step.path)}
                onKeyDown={(e) => complete && e.key === "Enter" && navigate(step.path)}
                role={complete ? "button" : undefined}
                tabIndex={complete ? 0 : undefined}
              >
                <div className="cv-progress-rail">
                  <span>{complete ? "✓" : index + 1}</span>
                </div>
                <div>
                  <div className="cv-progress-title">{step.label}</div>
                  <div className="cv-progress-description">{step.description}</div>
                </div>
              </div>
            );
          })}
        </aside>

        <button
          type="button"
          className="ti-back-link ce-mobile-nav-link"
          onClick={() => navigate("/content-engine")}
        >
          ← Back to Topics
        </button>

        <section className="cv-creator-card">
          <div className="cv-card-inner">
            <div className="ti-head">
              <p className="subtitle">
                <WhatshotIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-top", color: "#2364e8" }} />
                Pick a trending topic to get started
              </p>
              <button
                type="button"
                className="ti-back-link ce-desktop-nav-link"
                onClick={() => navigate("/content-engine")}
              >
                ← Back to Topics
              </button>
            </div>

            {loading && (
              <div className="loading-text">Loading trending topics…</div>
            )}

            <div className="topbar">
              <div className="filters">
                {loading
                  ? [1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} variant="rounded" height={34} width={90} sx={{ borderRadius: "8px" }} />
                    ))
                  : filters.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        className={`filter-chip${activeTag === item.value ? " active" : ""}`}
                        onClick={() => setActiveTag(activeTag === item.value ? "" : item.value)}
                      >
                        {item.label}
                      </button>
                    ))}
              </div>

              <div className="ti-search-wrap">
                <SearchIcon className="ti-search-icon" sx={{ fontSize: 18 }} />
                <input
                  className="search"
                  placeholder="Search topics…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setSearch("");
                  }}
                />
                {search && (
                  <button
                    type="button"
                    className="search-clear"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="grid">
              {loading
                ? [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={72}
                      sx={{ borderRadius: "14px" }}
                    />
                  ))
                : visibleTopics.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`topic${selecting ? " disabled" : ""}`}
                      onClick={() => !selecting && selectTopic(item)}
                      disabled={selecting}
                    >
                      <div className="topic-title">{item.title}</div>
                      <div className="tag">#{item.tag}</div>
                    </button>
                  ))}
            </div>

            {!loading && visibleCount < filteredTopics.length && (
              <div className="load-more-wrap">
                <button
                  type="button"
                  className="cv-btn cv-btn-secondary ti-load-more"
                  onClick={() => setVisibleCount((prev) => prev + 8)}
                >
                  Show more topics
                </button>
              </div>
            )}

            {!loading && topics.length === 0 && (
              <div className="hint">No trending topics found</div>
            )}

            {!loading && topics.length > 0 && (
              <div className="hint">
                <LightbulbOutlinedIcon sx={{ fontSize: 15, mr: 0.5, verticalAlign: "text-top" }} />
                Select any topic to continue to format selection
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TrendingIdeas;
